import { useCallback, useEffect, useRef, useState } from 'react';

import { isCreditsDegradationUiEnabled } from '../../common/feature-gates';
import {
	getBrowserLocalStorage,
	readLocalStorageItem,
	removeLocalStorageItem,
	type BrowserLocalStorage,
	type LocalStorageErrorHandler,
	writeLocalStorageItem,
} from '../../common/storage/local-storage';

/** @internal Exposed for tests only — consumers must not depend on this key. */
export const BANNER_DISMISSAL_STORAGE_KEY = 'rovo-credits-banner-dismissed';

let dismissalStorage: BrowserLocalStorage | null = null;

const getDismissalStorage = (onError?: LocalStorageErrorHandler): BrowserLocalStorage | null => {
	if (dismissalStorage === null) {
		dismissalStorage = getBrowserLocalStorage(onError);
	}

	return dismissalStorage;
};

const readStoredDismissal = (onError?: LocalStorageErrorHandler): boolean => {
	return (
		readLocalStorageItem(getDismissalStorage(onError), BANNER_DISMISSAL_STORAGE_KEY, onError) ===
		'true'
	);
};

const storeDismissal = (onError?: LocalStorageErrorHandler): void => {
	void writeLocalStorageItem(
		getDismissalStorage(onError),
		BANNER_DISMISSAL_STORAGE_KEY,
		'true',
		onError,
		{ reportBlocked: isCreditsDegradationUiEnabled() },
	);
};

const clearStoredDismissal = (onError?: LocalStorageErrorHandler): void => {
	removeLocalStorageItem(getDismissalStorage(onError), BANNER_DISMISSAL_STORAGE_KEY, onError);
};

const canListenForStorageEvents = (): boolean =>
	typeof window !== 'undefined' &&
	typeof window.addEventListener === 'function' &&
	typeof window.removeEventListener === 'function';

const isDismissalStorageEvent = (event: StorageEvent, storage: BrowserLocalStorage): boolean => {
	// `storage.storage` is the native Storage captured before browser storage controls install
	// their window.localStorage proxy, so it is reference-equal to `StorageEvent.storageArea`.
	// If that stops holding, cross-tab dismissal sync fails silently.
	if (event.storageArea !== storage.storage) {
		return false;
	}

	// `localStorage.clear()` emits `key === null`, so re-read the dismissal key.
	return event.key === BANNER_DISMISSAL_STORAGE_KEY || event.key === null;
};

export type DismissalPersistence = {
	isDismissed: boolean;
	dismissBanner: () => void;
	showBanner: () => void;
};

/**
 * Banner-dismissal state backed by `localStorage`.
 *
 * Dismissible warning banners hydrate from storage and listen for `storage`
 * events so a dismissal in one tab hides the same warning in other open tabs.
 * When the banner is not dismissible, any persisted flag is ignored and
 * cleared so non-dismissible states (e.g. `depleted-*`) cannot inherit a stale
 * dismissal from an earlier warning.
 *
 * Not SSR-safe: the server always sees `false`, so a dismissed banner will
 * flash on hydration. Consumers that render on the server should gate this
 * hook (or the banner itself) behind a client-only boundary.
 */
export const useDismissalPersistence = (
	isDismissible: boolean,
	onError?: LocalStorageErrorHandler,
): DismissalPersistence => {
	const [isBannerDismissed, setIsBannerDismissed] = useState<boolean>(
		() => isDismissible && readStoredDismissal(onError),
	);
	const hasAppliedStoredDismissalRef = useRef(isDismissible);

	const applyStoredDismissal = useCallback((): void => {
		setIsBannerDismissed(readStoredDismissal(onError));
	}, [onError]);

	// Dismissal only applies to dismissible warning banners. When the banner
	// becomes non-dismissible, clear storage so depleted banners cannot be hidden.
	useEffect(() => {
		if (isDismissible) {
			if (!hasAppliedStoredDismissalRef.current) {
				applyStoredDismissal();
				hasAppliedStoredDismissalRef.current = true;
			}
			return;
		}

		hasAppliedStoredDismissalRef.current = false;
		setIsBannerDismissed(false);
		clearStoredDismissal(onError);
	}, [isDismissible, onError, applyStoredDismissal]);

	// Browser `storage` events fire in other tabs, so this keeps banner
	// visibility aligned across open Rovo chat tabs.
	useEffect(() => {
		if (!isDismissible || !canListenForStorageEvents()) {
			return;
		}

		const browserStorage = getDismissalStorage(onError);
		if (browserStorage === null) {
			return;
		}

		const handleCrossTabStorageEvent = (event: StorageEvent): void => {
			if (isDismissalStorageEvent(event, browserStorage)) {
				applyStoredDismissal();
			}
		};

		window.addEventListener('storage', handleCrossTabStorageEvent);

		return () => {
			window.removeEventListener('storage', handleCrossTabStorageEvent);
		};
	}, [isDismissible, onError, applyStoredDismissal]);

	// Memoized so consumers can safely list these in effect deps.
	const dismissBanner = useCallback((): void => {
		setIsBannerDismissed(true);
		storeDismissal(onError);
	}, [onError]);

	const showBanner = useCallback((): void => {
		setIsBannerDismissed(false);
		clearStoredDismissal(onError);
	}, [onError]);

	const isDismissedForCurrentBanner = isDismissible && isBannerDismissed;

	return { isDismissed: isDismissedForCurrentBanner, dismissBanner, showBanner };
};
